import { type FC, startTransition, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    graphql,
    useFragment,
    usePreloadedQuery,
    type PreloadedQuery,
    useRefetchableFragment,
} from 'react-relay';
import classnames from 'classnames';

import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';
import { type VisibilityChangeCallback } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import {
    recentlyViewedItemsQuery,
    useRecentlyViewedItemsContext,
    MOBILE,
} from './RecentlyViewedItemsContext.tsx';
import {
    RecentlyViewedItemsWrapper,
    type RecentlyViewedItemsWrapperProps,
} from './RecentlyViewedItemsWrapper.tsx';
import { useRecentHistoryList } from './useRecentHistoryList.ts';
import { RecentlyViewedItemsHeart } from './RecentlyViewedItemsHeart.tsx';
import { ACTION_LABEL } from './constants.ts';
import { trackItemImpression, trackItemClick, trackArrowClick } from '../recentHistoryTracking.ts';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import { BuyerProductImageOnly } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import { type RecentlyViewedItemsCarousel_viewer$key } from './__generated__/RecentlyViewedItemsCarousel_viewer.graphql.ts';
import { type RecentlyViewedItemsCarouselViewerRefetchQuery } from './__generated__/RecentlyViewedItemsCarouselViewerRefetchQuery.graphql.ts';
import { type RecentlyViewedItemsCarousel_user$key } from './__generated__/RecentlyViewedItemsCarousel_user.graphql.ts';
import { type RecentlyViewedItemsContextQuery } from './__generated__/RecentlyViewedItemsContextQuery.graphql.ts';
import { type ItemType } from '../recentHistoryTypes.ts';

import dibsCss from 'dibs-css';

import { type RecentlyViewedItemsCarousel_items$key } from './__generated__/RecentlyViewedItemsCarousel_items.graphql.ts';

const viewerFragment = graphql`
    fragment RecentlyViewedItemsCarousel_viewer on Viewer
    @refetchable(queryName: "RecentlyViewedItemsCarouselViewerRefetchQuery")
    @argumentDefinitions(
        loadPortfolioData: { type: "Boolean", defaultValue: false }
        selectedItemIds: { type: "[String]", defaultValue: [] }
        userIds: { type: "[String]", defaultValue: [] }
    ) {
        ...RecentlyViewedItemsHeart_viewer
            @arguments(
                loadPortfolioData: $loadPortfolioData
                selectedItemIds: $selectedItemIds
                userIds: $userIds
            )
    }
`;

const userFragment = graphql`
    fragment RecentlyViewedItemsCarousel_user on User
    @argumentDefinitions(
        excludeItemPks: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 0 }
    ) {
        ...useRecentHistoryList_user @arguments(excludeItemPks: $excludeItemPks, count: $count)
        ...RecentlyViewedItemsHeart_user @arguments(excludeItemPks: $excludeItemPks, count: $count)
    }
`;

const itemsFragment = graphql`
    fragment RecentlyViewedItemsCarousel_items on Item @relay(plural: true) {
        ...useRecentHistoryList_items
        ...RecentlyViewedItemsHeart_items
    }
`;

type OnInitialDisplay = ({ items }: { items: ItemType[] }) => void;

export type RecentlyViewedItemsCarouselProps = {
    authModalLoader: $TSFixMe;
    queryReference: PreloadedQuery<RecentlyViewedItemsContextQuery>;
    trackItemClick?: typeof trackItemClick;
    trackItemImpression?: typeof trackItemImpression;
    onInitialDisplay?: OnInitialDisplay;
    isBot?: boolean;
} & Omit<RecentlyViewedItemsWrapperProps, 'children'>;

export const RecentlyViewedItemsCarousel: FC<RecentlyViewedItemsCarouselProps> = ({
    wrapperClassName,
    authModalLoader,
    queryReference,
    actionLabel = ACTION_LABEL,
    trackViewMoreClick,
    onInitialDisplay,
    hasTopSeparator = false,
    hasBottomSeparator = false,
    ...props
}) => {
    const data = usePreloadedQuery<RecentlyViewedItemsContextQuery>(
        recentlyViewedItemsQuery,
        queryReference
    );
    const user = useFragment<RecentlyViewedItemsCarousel_user$key>(
        userFragment,
        data.viewer?.user || null
    );
    const [viewer, refetch] = useRefetchableFragment<
        RecentlyViewedItemsCarouselViewerRefetchQuery,
        RecentlyViewedItemsCarousel_viewer$key
    >(viewerFragment, data.viewer);
    const itemsRef = useFragment<RecentlyViewedItemsCarousel_items$key>(
        itemsFragment,
        data?.viewer?.itemRead ? data.viewer.itemRead.filter(filterNulls) : null
    );

    const { items, hasFetchedItems } = useRecentHistoryList({ user, itemsRef });
    const { userId, deviceType, itemsPerPage } = useRecentlyViewedItemsContext();
    const isMobile = deviceType === MOBILE;
    const itemsIds = useMemo(() => items?.map(item => item.itemId), [items]);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const onVisibilityChange: (props: VisibilityChangeCallback) => void = useCallback(
        ({ isVisible, disconnect }) => {
            if (isVisible && onInitialDisplay && items?.length) {
                onInitialDisplay({ items });
                disconnect();
            }
        },
        [onInitialDisplay, items]
    );

    useVisibilityTracker({
        elementRef: wrapperRef,
        onVisibilityChange: onVisibilityChange,
        isEnabled: !!onInitialDisplay,
    });

    useEffect(() => {
        /**
         * Refetch favorites data if recent history contains fetched items
         */
        if (hasFetchedItems && itemsIds && userId) {
            // refetch suspends, and this runs after the carousel is already on screen. Without a
            // transition the boundary falls back to the loading shimmer and the items disappear.
            startTransition(() => {
                refetch({
                    loadPortfolioData: true,
                    selectedItemIds: itemsIds,
                    userIds: userId ? [userId] : [],
                });
            });
        }
    }, [refetch, itemsIds, userId, hasFetchedItems]);

    if (!items?.length) {
        return null;
    }

    return (
        <RecentlyViewedItemsWrapper
            wrapperClassName={wrapperClassName}
            actionLabel={actionLabel}
            trackViewMoreClick={trackViewMoreClick}
            hasTopSeparator={hasTopSeparator}
            hasBottomSeparator={hasBottomSeparator}
            ref={wrapperRef}
        >
            <ScrollCarousel
                animation="slide"
                enableScroll
                itemsToShow={itemsPerPage}
                dataTn="recently-viewed-items-carousel"
                noGap
                gapSize="small"
                {...(isMobile
                    ? {
                          stepSize: 1,
                          scrollPadding: 'small',
                      }
                    : {
                          stepSize: itemsPerPage,
                          isInfinite: true,
                          showArrows: true,
                          onArrowClick: ({ direction }) =>
                              trackArrowClick({ direction, actionLabel }),
                      })}
            >
                {items.map((item, index) => (
                    <BuyerProductImageOnly
                        key={index}
                        item={item}
                        dataTn="recently-viewed-tile"
                        imageBackgroundColor="moonStone"
                        index={index}
                        itemsPerRow={0} // lazy load all images in the carousel
                        onClick={() => {
                            (props.trackItemClick || trackItemClick)({
                                item,
                                index,
                                actionLabel,
                            });
                        }}
                        onContentLoaded={() => {
                            (props.trackItemImpression || trackItemImpression)({
                                items: [item],
                                index,
                                actionLabel,
                            });
                        }}
                        size="small"
                        srcSet={{ sizes: getSrcsetString(item.itemImageUrl) }}
                        favoritesComponent={
                            <div
                                className={classnames(
                                    dibsCss.absolute,
                                    dibsCss.top0,
                                    dibsCss.right0,
                                    dibsCss.mtXxsmall,
                                    dibsCss.prXxsmall
                                )}
                            >
                                <RecentlyViewedItemsHeart
                                    isMobile={isMobile}
                                    currentItem={item}
                                    viewer={viewer}
                                    user={user}
                                    items={itemsRef}
                                    authModalLoader={authModalLoader}
                                />
                            </div>
                        }
                    />
                ))}
            </ScrollCarousel>
        </RecentlyViewedItemsWrapper>
    );
};
